/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IControllerType } from '@/redux/post/post.d';

export type Step =
  | 'idle'
  | 'editing'
  | 'askShareTools'
  | 'applyToGroup'
  | 'finalized';

export interface FileItem {
  id: string;
  name: string;
  controllerType: IControllerType;
  previewUrl?: string;
}

export interface ToolData {
  modelId?: string; // Added to track which model this tool belongs to
  laserOrEndmill: string;
  bitId?: string; // Catalog bit reference (Endmill bit, or the selected Laser)
  brandId?: string; // Which of the bit's brands was picked
  spindleSpeed?: number; // Spindle speed in RPM (6000-24000, Endmill only)
}

export interface GroupConfig {
  groupName: string;
  controllerType: IControllerType;
  toolDatas: ToolData[]; // support multiple tools per group
  fileIds: string[];
}

export interface ModelToolMappingWorkflowState {
  step: Step;
  selectedFileId: string | null;
  derivedGroupName: string;
  toolDataDraft: ToolData | null;
  // Index of the existing tool being edited within the current group, or null
  // when adding a new tool. When set, saving replaces that tool instead of
  // appending (so editing a tool's bit doesn't create a duplicate).
  editingToolIndex: number | null;
  applyToFileIds: string[];
  completedGroups: GroupConfig[];
  shouldShareTools: boolean;
}

const initialState: ModelToolMappingWorkflowState = {
  step: 'idle',
  selectedFileId: null,
  derivedGroupName: '',
  toolDataDraft: null,
  editingToolIndex: null,
  applyToFileIds: [],
  completedGroups: [],
  shouldShareTools: false,
};

const modelToolMappingWorkflowSlice = createSlice({
  name: 'modelToolMappingWorkflow',
  initialState,
  reducers: {
    startEditing: (
      state,
      action: PayloadAction<{
        fileId: string;
        fileName: string;
        groupName: string;
      }>
    ) => {
      const { fileId, groupName } = action.payload;
      state.selectedFileId = fileId;
      // Ensure unique group name by appending incremental numbers on collision
      const baseName = groupName;
      const existingNames = new Set(
        state.completedGroups.map((g) => g.groupName)
      );
      let uniqueName = baseName;
      let suffix = 2;
      while (existingNames.has(uniqueName)) {
        uniqueName = `${baseName}-${suffix}`;
        suffix += 1;
      }
      state.derivedGroupName = uniqueName;
      state.toolDataDraft = null;
      state.editingToolIndex = null;
      state.applyToFileIds = [];
      state.shouldShareTools = false;
      state.step = 'editing';
    },
    updateToolDataDraft: (state, action: PayloadAction<ToolData>) => {
      const newToolData = action.payload;
      const previousToolData = state.toolDataDraft;

      // If switching between LASER and ENDMILL (in either direction), clear existing tools for this file
      if (
        previousToolData &&
        previousToolData.laserOrEndmill !== newToolData.laserOrEndmill &&
        state.selectedFileId &&
        state.derivedGroupName
      ) {
        // Find the group and remove all tools for the current file
        const groupIndex = state.completedGroups.findIndex(
          (g) => g.groupName === state.derivedGroupName
        );
        if (groupIndex >= 0) {
          const group = state.completedGroups[groupIndex];
          // Remove tools that belong to the current file
          const filteredTools = (group.toolDatas || []).filter(
            (tool: any) => tool.modelId !== state.selectedFileId
          );
          state.completedGroups[groupIndex] = {
            ...group,
            toolDatas: filteredTools,
          };
        }
      }

      state.toolDataDraft = newToolData;
    },
    setShouldShareTools: (state, action: PayloadAction<boolean>) => {
      state.shouldShareTools = action.payload;
    },
    answerShareTools: (state, action: PayloadAction<boolean>) => {
      const shouldShare = action.payload;
      state.shouldShareTools = shouldShare;

      if (shouldShare) {
        // User selected Yes - go to file selection page
        state.step = 'applyToGroup';
      }
      // If No, step will be handled by caller to complete immediately
    },
    completeToolData: (state) => {
      // Check if we have tools in the current group
      const currentGroup = state.completedGroups.find(
        (g) => g.groupName === state.derivedGroupName
      );
      const hasToolsInGroup =
        currentGroup &&
        currentGroup.toolDatas &&
        currentGroup.toolDatas.length > 0;

      // Only proceed if we have a draft OR if we have tools in the group
      if (!state.toolDataDraft && !hasToolsInGroup) return;

      // Transition to askShareTools step to show Yes/No question page
      state.step = 'askShareTools';
    },
    setApplySelection: (state, action: PayloadAction<string[]>) => {
      state.applyToFileIds = action.payload;
    },
    addToolToGroup: (
      state,
      action: PayloadAction<{ controllerType: IControllerType }>
    ) => {
      const { toolDataDraft, derivedGroupName, selectedFileId } = state;
      if (!toolDataDraft || !derivedGroupName) return;

      // For LASER, only allow one tool per file
      if (toolDataDraft.laserOrEndmill === 'LASER') {
        const existingIdx = state.completedGroups.findIndex(
          (g) => g.groupName === derivedGroupName
        );
        if (existingIdx >= 0) {
          const existing = state.completedGroups[existingIdx];
          // Check if there's already a LASER tool for this file
          const hasLaserTool = (existing.toolDatas || []).some(
            (tool: any) => tool.laserOrEndmill === 'LASER'
          );
          if (hasLaserTool) {
            // Don't add another LASER tool, just return
            return;
          }
        }
      }

      const existingIdx = state.completedGroups.findIndex(
        (g) => g.groupName === derivedGroupName
      );
      if (existingIdx >= 0) {
        const existing = state.completedGroups[existingIdx];
        const baseTools = existing.toolDatas || [];
        const editIdx = state.editingToolIndex;
        // Editing an existing tool -> replace it in place; otherwise append.
        const withTool =
          editIdx !== null && editIdx >= 0 && editIdx < baseTools.length
            ? baseTools.map((t, i) => (i === editIdx ? toolDataDraft : t))
            : [...baseTools, toolDataDraft];
        const withFile = selectedFileId
          ? Array.from(new Set([...(existing.fileIds || []), selectedFileId]))
          : existing.fileIds || [];
        state.completedGroups[existingIdx] = {
          ...existing,
          toolDatas: withTool,
          fileIds: withFile,
        };
      } else {
        state.completedGroups.push({
          groupName: derivedGroupName,
          controllerType: action.payload.controllerType,
          toolDatas: [toolDataDraft],
          fileIds: selectedFileId ? [selectedFileId] : [],
        } as unknown as GroupConfig);
      }

      // Clear the draft + edit pointer after adding/replacing.
      state.toolDataDraft = null;
      state.editingToolIndex = null;
    },
    completeGroup: (
      state,
      action: PayloadAction<{ controllerType: IControllerType }>
    ) => {
      const {
        selectedFileId,
        toolDataDraft,
        derivedGroupName,
        applyToFileIds,
      } = state;

      // When editing an existing group, selectedFileId and toolDataDraft might be null
      // if the user already added tools and just wants to complete
      const existingIdx = state.completedGroups.findIndex(
        (g) => g.groupName === derivedGroupName
      );

      // If editing an existing group with no pending draft
      if (existingIdx >= 0 && !toolDataDraft) {
        // If there are files to add (from applyToGroup step), add them
        if (applyToFileIds.length > 0) {
          const existing = state.completedGroups[existingIdx];
          const merged = Array.from(
            new Set([...(existing.fileIds || []), ...applyToFileIds])
          );
          state.completedGroups[existingIdx] = {
            ...existing,
            fileIds: merged,
          };
        }
        // Reset state and transition back to idle
        state.selectedFileId = null;
        state.toolDataDraft = null;
        state.editingToolIndex = null;
        state.applyToFileIds = [];
        state.derivedGroupName = '';
        state.step = 'idle';
        return;
      }

      // For new groups, require both selectedFileId and toolDataDraft
      if (!selectedFileId || !toolDataDraft) return;

      const fileIds = [selectedFileId, ...applyToFileIds];

      const isEditingExistingGroup = existingIdx >= 0;

      if (isEditingExistingGroup) {
        // Merge files into existing group; replace the edited tool in place
        // (editingToolIndex set) or append a new one.
        const existing = state.completedGroups[existingIdx];
        const merged = Array.from(
          new Set([...(existing.fileIds || []), ...fileIds])
        );
        const baseTools = existing.toolDatas || [];
        const editIdx = state.editingToolIndex;
        const nextTools =
          editIdx !== null && editIdx >= 0 && editIdx < baseTools.length
            ? baseTools.map((t, i) => (i === editIdx ? toolDataDraft : t))
            : [...baseTools, toolDataDraft];
        state.completedGroups[existingIdx] = {
          ...existing,
          fileIds: merged,
          toolDatas: nextTools,
        };
      } else {
        const group: GroupConfig = {
          groupName: derivedGroupName,
          controllerType: action.payload.controllerType,
          toolDatas: [toolDataDraft],
          fileIds,
        };
        state.completedGroups.push(group);
      }
      state.selectedFileId = null;
      state.toolDataDraft = null;
      state.editingToolIndex = null;
      state.applyToFileIds = [];
      state.derivedGroupName = '';
      state.step = 'idle';
    },
    deleteGroup: (state, action: PayloadAction<{ groupName: string }>) => {
      const { groupName } = action.payload;
      state.completedGroups = state.completedGroups.filter(
        (g) => g.groupName !== groupName
      );
      // If user is in finalized view and a group is deleted, return to idle so they can add tool data again
      if (state.step === 'finalized') {
        state.step = 'idle';
      }
    },
    deleteToolFromGroup: (
      state,
      action: PayloadAction<{ groupName: string; toolIndex: number }>
    ) => {
      const { groupName, toolIndex } = action.payload;
      const idx = state.completedGroups.findIndex(
        (g) => g.groupName === groupName
      );
      if (idx < 0) return;
      const group = state.completedGroups[idx];
      const tools = Array.isArray(group.toolDatas) ? [...group.toolDatas] : [];
      if (toolIndex < 0 || toolIndex >= tools.length) return;
      tools.splice(toolIndex, 1);

      // If no tools left after deletion, remove the entire group and return to idle
      if (tools.length === 0) {
        state.completedGroups = state.completedGroups.filter(
          (g) => g.groupName !== groupName
        );
        if (state.step === 'finalized') {
          state.step = 'idle';
        }
      } else {
        state.completedGroups[idx] = { ...group, toolDatas: tools };
      }
    },
    updateGroupName: (
      state,
      action: PayloadAction<{ oldGroupName: string; newGroupName: string }>
    ) => {
      const { oldGroupName, newGroupName } = action.payload;
      const idx = state.completedGroups.findIndex(
        (g) => g.groupName === oldGroupName
      );
      if (idx < 0) return;

      // Check if new name is unique
      const existingNames = new Set(
        state.completedGroups
          .filter((g) => g.groupName !== oldGroupName)
          .map((g) => g.groupName)
      );

      if (existingNames.has(newGroupName)) {
        return; // Don't update if name already exists
      }

      state.completedGroups[idx] = {
        ...state.completedGroups[idx],
        groupName: newGroupName,
      };
    },
    setFinalized: (state) => {
      state.step = 'finalized';
    },
    resetWorkflow: () => initialState,
    loadExistingGroups: (state, action: PayloadAction<GroupConfig[]>) => {
      state.completedGroups = action.payload;
      if (action.payload.length > 0) {
        state.step = 'finalized';
      }
    },
    // editExistingGroup: branchy by nature (first-tool pre-fill + nullish fallbacks)
    // fallow-ignore-next-line complexity
    editExistingGroup: (
      state,
      action: PayloadAction<{ groupName: string }>
    ) => {
      const { groupName } = action.payload;
      const group = state.completedGroups.find(
        (g) => g.groupName === groupName
      );
      if (!group) return;

      // Pre-load the group's first tool into the editor draft so the bit picker
      // shows the bit that's already configured (instead of an empty select).
      // The tool STAYS in the group (the panel keeps listing it); saving
      // replaces it in place via editingToolIndex, so there's no duplicate.
      // Groups are typically single-tool; multi-tool groups edit the first.
      const firstTool = group.toolDatas?.[0];

      state.step = 'editing';
      state.derivedGroupName = groupName;
      state.applyToFileIds = [];
      state.shouldShareTools = false;
      state.toolDataDraft = firstTool ? { ...firstTool } : null;
      state.editingToolIndex = firstTool ? 0 : null;
      // Select the file the edited tool maps to (fallback: first file in group).
      state.selectedFileId = firstTool?.modelId ?? group.fileIds?.[0] ?? null;
    },
  },
});

export const {
  startEditing,
  updateToolDataDraft,
  setShouldShareTools,
  answerShareTools,
  completeToolData,
  setApplySelection,
  addToolToGroup,
  completeGroup,
  deleteGroup,
  deleteToolFromGroup,
  updateGroupName,
  setFinalized,
  resetWorkflow,
  loadExistingGroups,
  editExistingGroup,
} = modelToolMappingWorkflowSlice.actions;

export default modelToolMappingWorkflowSlice.reducer;
